import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { Control, FieldErrors, UseFormGetValues, UseFormRegister, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import type { Lead } from '../../../entities/lead/model/types';
import {
  buildLeadFormValues,
  buildLeadPayload,
  type LeadFormValues,
} from '../../create-lead/model/leadForm';

export interface CardModalLeadFormHook {
  isReady: boolean;
  control: Control<LeadFormValues>;
  register: UseFormRegister<LeadFormValues>;
  errors: FieldErrors<LeadFormValues>;
  submit: React.FormEventHandler<HTMLFormElement>;
  setValue: UseFormSetValue<LeadFormValues>;
  getValues: UseFormGetValues<LeadFormValues>;
  watch: UseFormWatch<LeadFormValues>;
}

export const useCardModalLeadForm = (
  lead: Lead | null,
  onSave: (data: Partial<Lead>) => Promise<void> | void,
): CardModalLeadFormHook => {
  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm<LeadFormValues>({
    defaultValues: buildLeadFormValues(lead),
  });

  const leadId = lead?.id ?? null;
  const [confirmedLeadId, setConfirmedLeadId] = useState(leadId);
  const isReady = confirmedLeadId === leadId;

  useEffect(() => {
    reset(buildLeadFormValues(lead));
    setConfirmedLeadId(leadId);
  }, [leadId, lead, reset]);

  const submit = handleSubmit(async formValues => {
    await onSave(buildLeadPayload(formValues));
  });

  return {
    isReady,
    control,
    register,
    errors,
    submit,
    setValue,
    getValues,
    watch,
  };
};
